import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Sparkles, Star } from 'lucide-react';

export const CinematicSponsorWall = ({ tournamentId, isEsports }) => {
    const [sponsors, setSponsors] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSponsors = async () => {
            if (!tournamentId) return;
            try {
                const { data, error } = await supabase
                    .from('sponsors')
                    .select('id, name, logo_url, website_url, display_duration')
                    .eq('tournament_id', tournamentId)
                    .eq('is_active', true)
                    .order('created_at', { ascending: false });

                if (error) throw error;
                // Filter out any without logos
                const validSponsors = (data || []).filter(s => s.logo_url && s.logo_url.trim() !== '');
                setSponsors(validSponsors);
            } catch (err) {
                console.error('Error loading sponsors for Cinematic Wall:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSponsors();
    }, [tournamentId]);

    // Timer logic for cycling
    useEffect(() => {
        if (sponsors.length <= 1) return;

        const currentDuration = sponsors[currentIndex]?.display_duration || 5;
        
        const timer = setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % sponsors.length);
        }, currentDuration * 1000);

        return () => clearTimeout(timer);
    }, [currentIndex, sponsors]);

    if (loading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-600 border-t-white"></div>
            </div>
        );
    }

    if (sponsors.length === 0) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center text-white/50 text-center p-8">
                <Star size={64} className="mb-6 opacity-20" />
                <h2 className="text-3xl font-bold mb-2">Premium Sponsor Wall</h2>
                <p className="text-xl">Voeg sponsoren met een logo toe in de beheeromgeving om ze hier in de spotlight te zetten!</p>
            </div>
        );
    }

    return (
        <div className="relative w-full h-full overflow-hidden flex items-center justify-center z-10">
            {/* Cinematic Background Glow */}
            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[120px] transition-all duration-1000 z-0 ${
                isEsports ? 'bg-purple-600/30' : 'bg-amber-500/20'
            }`}></div>

            {/* Subtle floating particles */}
            <div className="absolute inset-0 opacity-30 z-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white] animate-pulse"></div>
                <div className="absolute top-3/4 left-2/3 w-3 h-3 bg-white rounded-full shadow-[0_0_15px_white] animate-pulse" style={{ animationDelay: '1s' }}></div>
                <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-white rounded-full shadow-[0_0_8px_white] animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Central Content */}
            <div className="relative z-20 w-full max-w-5xl px-8 flex flex-col items-center text-center">
                
                {/* Intro Title */}
                <div className={`mb-12 flex items-center gap-4 px-8 py-3 rounded-full border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl animate-fade-in-up`}>
                    <Sparkles className={isEsports ? 'text-purple-400' : 'text-amber-400'} size={24} />
                    <span className="text-white text-xl font-medium tracking-widest uppercase">Mede mogelijk gemaakt door</span>
                    <Sparkles className={isEsports ? 'text-purple-400' : 'text-amber-400'} size={24} />
                </div>

                {/* Rotating Sponsor Logo */}
                <div className="relative w-full h-[50vh] flex items-center justify-center perspective-1000">
                    {sponsors.map((sponsor, idx) => {
                        const isActive = idx === currentIndex;
                        return (
                            <div 
                                key={sponsor.id}
                                className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-1000 transform ${
                                    isActive 
                                        ? 'opacity-100 scale-100 translate-y-0 rotate-x-0' 
                                        : 'opacity-0 scale-90 translate-y-8 rotate-x-12 pointer-events-none'
                                }`}
                            >
                                {/* Logo Box with Glow */}
                                <div className={`relative p-8 md:p-16 rounded-3xl bg-white shadow-2xl transition-all duration-700 ${
                                    isActive ? 'shadow-[0_0_80px_rgba(255,255,255,0.2)]' : ''
                                }`}>
                                    <img 
                                        src={sponsor.logo_url} 
                                        alt={sponsor.name}
                                        className="max-h-[30vh] max-w-full object-contain filter drop-shadow-lg"
                                    />
                                    
                                    {/* Gold/Purple border overlay */}
                                    <div className={`absolute inset-0 rounded-3xl border-4 opacity-50 ${
                                        isEsports ? 'border-purple-500' : 'border-amber-400'
                                    }`}></div>
                                </div>
                                
                                {/* Sponsor Name */}
                                <h3 className={`mt-12 text-5xl md:text-7xl font-black tracking-tight text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)] transition-opacity duration-1000 delay-300 ${
                                    isActive ? 'opacity-100' : 'opacity-0'
                                }`}>
                                    {sponsor.name}
                                </h3>

                                {/* Website (optional) */}
                                {sponsor.website_url && (
                                    <p className={`mt-4 text-xl text-white/60 font-medium tracking-wider transition-opacity duration-1000 delay-500 ${
                                        isActive ? 'opacity-100' : 'opacity-0'
                                    }`}>
                                        {sponsor.website_url.replace(/^https?:\/\//, '')}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Progress Indicators */}
                {sponsors.length > 1 && (
                    <div className="absolute bottom-[-10vh] flex gap-3">
                        {sponsors.map((_, idx) => (
                            <div 
                                key={idx} 
                                className={`h-2 rounded-full transition-all duration-500 ${
                                    idx === currentIndex 
                                        ? `w-12 ${isEsports ? 'bg-purple-500' : 'bg-amber-400'}` 
                                        : 'w-4 bg-white/20'
                                }`}
                            ></div>
                        ))}
                    </div>
                )}
            </div>

            {/* CSS for custom animations */}
            <style>{`
                .perspective-1000 {
                    perspective: 1000px;
                }
                .rotate-x-12 {
                    transform: rotateX(12deg) scale(0.9) translateY(2rem);
                }
                .rotate-x-0 {
                    transform: rotateX(0deg) scale(1) translateY(0);
                }
                @keyframes fade-in-up {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 1s ease-out forwards;
                }
            `}</style>
        </div>
    );
};
